/* Article page behaviour (no framework; base.js runs the site chrome). The
   page is complete without it: numbers and notes are plain links, menus are
   server-rendered [hidden]. This adds share, reading progress and the
   running head, fact-check margin alignment, glossary popovers (the entry
   fetched as an htmx partial), the "Hide fact checks" margin toggle (from
   1100px; a fact-check mark clicked while hidden reopens the margin at its
   note), the notes list below 1100px (the first three, then a button for the
   rest), the read credit and the reading report. Sources are not in the
   margin: a citation number jumps to its row in the Sources list. */
(() => {
  'use strict'
  const doc = document
  const root = doc.documentElement
  const $ = (sel, el = doc) => el.querySelector(sel)
  const $$ = (sel, el = doc) => Array.from(el.querySelectorAll(sel))
  const wide = () => matchMedia('(min-width: 1100px)').matches

  /* Share: native share sheet where the browser has one, else the menu (copy link
     + intent links). Each [data-share] is independent; a click outside one closes it. */
  const shares = $$('[data-share]')
  /* Each share's placement as the server rendered it (pg-share-up or not). */
  const shareUp = new Map(shares.map((s) => [s, s.classList.contains('pg-art-share-up')]))
  /* The menu's horizontal anchor: 'start' or 'end' for this open, null for the stylesheet's own side. */
  const anchorMenu = (menu, edge) => {
    menu.style.insetInlineStart = edge === 'start' ? '0px' : edge === 'end' ? 'auto' : ''
    menu.style.insetInlineEnd = edge === 'end' ? '0px' : edge === 'start' ? 'auto' : ''
  }
  const closeShare = (share) => {
    const menu = $('.pg-art-share-menu', share)
    if (menu.hidden) return
    menu.hidden = true
    anchorMenu(menu, null)
    share.classList.toggle('pg-art-share-up', shareUp.get(share))
    $('.pg-art-share-btn', share).setAttribute('aria-expanded', 'false')
  }
  /* An open menu takes the side that fits the window: its server-rendered side, else the other one. */
  const placeShareMenu = (share) => {
    const menu = $('.pg-art-share-menu', share)
    const fits = (up) => {
      share.classList.toggle('pg-art-share-up', up)
      const r = menu.getBoundingClientRect()
      return up ? r.top >= 0 : r.bottom <= innerHeight
    }
    const base = shareUp.get(share)
    share.classList.toggle('pg-art-share-up', fits(base) ? base : fits(!base) ? !base : base)
    clampMenuX(share, menu)
  }
  /* Off either side of the window by more than 8px, the menu takes the other inline anchor for this open
     (kept only when that overhangs less). Physical edges, so it holds in RTL too. */
  const clampMenuX = (share, menu) => {
    const overhang = () => {
      const r = menu.getBoundingClientRect()
      return Math.max(0, 8 - r.left) + Math.max(0, r.right - (innerWidth - 8))
    }
    anchorMenu(menu, null)
    const own = overhang()
    if (!own) return
    const def = share.classList.contains('pg-art-share-up') ? 'start' : 'end'
    anchorMenu(menu, def === 'start' ? 'end' : 'start')
    if (overhang() >= own) anchorMenu(menu, null)
  }
  const closeShares = (except) => { for (const s of shares) if (s !== except) closeShare(s) }
  /* Escape closes the glossary popover and the share menus; focus that was inside an open menu goes back to its Share button. */
  doc.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return
    const open = shares.find((s) => $('.pg-art-share-menu', s).contains(doc.activeElement))
    closePopover()
    closeShares()
    if (open) $('.pg-art-share-btn', open).focus()
  })
  doc.addEventListener('click', (e) => { for (const s of shares) if (!s.contains(e.target)) closeShare(s) })
  for (const share of shares) {
    const btn = $('.pg-art-share-btn', share)
    const menu = $('.pg-art-share-menu', share)
    btn.addEventListener('click', async () => {
      if (navigator.share) {
        try {
          await navigator.share({ title: share.dataset.title, url: share.dataset.url })
          return
        } catch (err) {
          if (err?.name === 'AbortError') return
          console.warn('[article] native share failed, showing the menu:', err)
        }
      }
      closeShares(share)
      menu.hidden = !menu.hidden
      if (!menu.hidden) placeShareMenu(share)
      btn.setAttribute('aria-expanded', String(!menu.hidden))
    })
    $('[data-share-copy]', share)?.addEventListener('click', async (e) => {
      const b = e.currentTarget
      const text = $('[data-share-label]', b) ?? b
      const label = b.dataset.label ?? (b.dataset.label = text.textContent)
      try {
        await navigator.clipboard.writeText(share.dataset.url)
        text.textContent = b.dataset.copied
        setTimeout(() => { text.textContent = label }, 1800)
      } catch (err) { console.warn('[article] copy failed:', err) }
    })
  }

  const article = $('article[data-article]')
  if (!article) return

  /* One read credit per view (the server recorded the document hit; the backend dedups the pair). */
  fetch(`/api/v1/news/articles/${encodeURIComponent(article.dataset.article)}/view`, { method: 'POST', credentials: 'include', keepalive: true })
    .catch((err) => console.warn('[article] view credit failed:', err))

  /* Reading report: how long the article has been on screen and how far down it the
     reader got, sent each time the page is hidden (the server keeps one visit's latest). */
  {
    const readUrl = `/api/v1/news/articles/${encodeURIComponent(article.dataset.article)}/read`
    let shownMs = 0
    let since = doc.visibilityState === 'visible' ? performance.now() : 0
    let depth = 0
    let pending = 0
    let sent = -1
    const measure = () => {
      pending = 0
      const top = article.getBoundingClientRect().top + scrollY
      const reach = (scrollY + innerHeight - top) / Math.max(1, article.offsetHeight)
      depth = Math.max(depth, Math.min(1, Math.max(0, reach)))
    }
    measure()
    addEventListener('scroll', () => { if (!pending) pending = requestAnimationFrame(measure) }, { passive: true })
    const report = () => {
      if (since) { shownMs += performance.now() - since; since = 0 }
      const ms = Math.round(shownMs)
      if (ms < 1000 || ms === sent) return
      sent = ms
      fetch(readUrl, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ms, depth: Math.round(depth * 100) / 100 }),
      }).catch((err) => console.warn('[article] reading report failed:', err))
    }
    doc.addEventListener('visibilitychange', () => {
      if (doc.visibilityState === 'hidden') report()
      else since = performance.now()
    })
    addEventListener('pagehide', report)
  }

  /* The filed-at stamp in the reader's own zone (the server renders UTC). */
  const stamp = $('time[data-pxke-stamp]', article)
  if (stamp) {
    const d = new Date(stamp.getAttribute('datetime'))
    const tag = stamp.dataset.locale || 'en'
    stamp.textContent = `\u2068${d.toLocaleDateString(tag, { day: 'numeric', month: 'short', year: 'numeric' })} · ${d.toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit', hour12: false })}\u2069`
  }

  /* Reading progress + running head (dead band so a reader resting on the line does not flip it). */
  const bar = $('[data-progress] span')
  const progressEl = $('[data-progress]')
  const sticky = $('[data-sticky]')
  const remaining = $('[data-remaining]', sticky || doc)
  const masthead = $('[data-masthead]')
  const title = $('[data-headline]')
  let stickyOn = false
  let raf = 0
  const update = () => {
    raf = 0
    const top = article.getBoundingClientRect().top + scrollY
    const span = Math.max(1, article.offsetHeight - innerHeight * 0.55)
    const p = Math.min(1, Math.max(0, (scrollY - top + 48) / span))
    if (bar) bar.style.transform = `scaleX(${p})`
    progressEl?.setAttribute('aria-valuenow', String(Math.round(p * 100)))
    if (remaining) {
      const shown = p >= 0.5 && p < 0.75
      remaining.classList.toggle('pg-art-shown', shown)
      remaining.textContent = remaining.dataset.remaining.replace('{pct}', window.pxke.formatNumber(shown ? Math.round((1 - p) * 100) : 50))
    }
    const bottom = title.getBoundingClientRect().bottom
    const next = stickyOn ? bottom <= 56 + 24 : bottom < 56 - 24
    if (next !== stickyOn) {
      stickyOn = next
      sticky?.classList.toggle('pg-art-on', next)
      sticky?.setAttribute('aria-hidden', String(!next))
      if (sticky) sticky.inert = !next
      masthead?.classList.toggle('pg-art-reading-collapsed', next)
    }
  }
  const schedule = () => { if (!raf) raf = requestAnimationFrame(update) }
  addEventListener('scroll', schedule, { passive: true })
  addEventListener('resize', schedule, { passive: true })
  /* Hidden running head: inert (no focus, no clicks) until it shows. */
  if (sticky) sticky.inert = true
  update()

  /* Margin notes level with what they annotate, never overlapping (lib/marginNotes stackNotes). */
  const notes = $('[data-margin-notes]')
  const body = $('[data-pxke-body]')
  const anchorFor = (li) => {
    if (li.id.startsWith('margin-receipt-')) return body.querySelector(`mark.pg-art-receipt-mark[data-n="${li.id.slice(15)}"], .pg-art-receipt-cell[data-receipt-n="${li.id.slice(15)}"]`)
    if (li.dataset.slug) return body.querySelector(`a.pg-art-glossary-term[data-glossary-slug="${CSS.escape(li.dataset.slug)}" i]`)
    return null
  }
  /* Drift guard: a note pushed more than DRIFT px below what it annotates
     makes room for the next ones (a glossary note steps aside, its term
     already links inline) until the notes are back near their anchors. */
  const DRIFT = 200
  const GAP = 18
  /* One pass: read every anchor top and note height first, place the notes
     in memory, then write only what changed. */
  const layout = () => {
    if (!notes || !notes.getClientRects().length) return
    if (!wide()) {
      /* Below 1100px the notes are a list in the flow: nothing is placed. */
      for (const li of notes.children) li.classList.remove('pg-art-aligned', 'pg-art-drift-skip')
      for (const li of notes.children) li.style.top = ''
      notes.style.height = ''
      notes.classList.add('pg-art-is-aligned')
      return
    }
    const items = Array.from(notes.children)
    for (const li of items) {
      li.classList.remove('pg-art-drift-skip')
      li.classList.add('pg-art-aligned')
    }
    const listTop = notes.getBoundingClientRect().top
    const wants = items.map((li) => { const a = anchorFor(li); return a ? a.getBoundingClientRect().top - listTop - 4 : null })
    const heights = items.map((li) => li.offsetHeight)
    let cursor = 0
    const plan = items.map((li, i) => {
      const want = wants[i]
      const behind = want == null ? 0 : cursor - want
      if (behind > DRIFT && li.classList.contains('pg-art-gloss-note')) return { li, skip: true, top: null }
      const top = Math.round(Math.max(want ?? cursor, cursor))
      cursor = top + heights[i] + GAP
      return { li, skip: false, top }
    })
    for (const { li, skip, top } of plan) {
      if (skip) li.classList.add('pg-art-drift-skip')
      const value = skip ? '' : `${top}px`
      if (li.style.top !== value) li.style.top = value
    }
    const height = `${Math.max(0, Math.round(cursor - (items.length ? GAP : 0)))}px`
    if (notes.style.height !== height) notes.style.height = height
    notes.classList.add('pg-art-is-aligned')
  }
  /* Every trigger (first run, fonts, image loads, resizes, an expander toggle, the notes toggle) lands in one frame. */
  let lr = 0
  const relayout = () => { if (notes && !lr) lr = requestAnimationFrame(() => { lr = 0; layout() }) }
  if (notes) {
    new ResizeObserver(relayout).observe(article)
    doc.fonts?.ready.then(relayout).catch((e) => console.warn('[article] fonts not ready:', e))
    /* Images and opened evidence move the text or the notes: align again. */
    for (const im of $$('img', article)) if (!im.complete) im.addEventListener('load', relayout, { once: true })
    notes.addEventListener('toggle', relayout, true)
    relayout()
  }

  /* Below 1100px the list shows its first three notes; "Show fact checks (N)" shows the rest, once.
     The notes it reveals get .pg-art-is-revealed (a short fade and rise, article_page.css).
     Without script the button stays hidden and every note shows. */
  const NOTES_FIRST = 3
  const notesMore = $('[data-notes-more]')
  const narrowNotes = matchMedia('(max-width: 1099.98px)')
  const applyNotesCollapse = () => {
    if (!notes || !notesMore) return
    const total = notes.children.length
    const collapsed = narrowNotes.matches && total > NOTES_FIRST && !notesMore.dataset.expanded
    notes.toggleAttribute('data-collapsed', collapsed)
    notesMore.hidden = !collapsed
    if (collapsed) notesMore.textContent = `${notesMore.dataset.label} (${window.pxke.formatNumber(total)})`
    relayout()
  }
  /* A note inside the collapsed rest opens the list first, so the caller can scroll to it. */
  const expandNotesFor = (el) => {
    if (!notesMore || !el?.closest('[data-collapsed]')) return
    notesMore.dataset.expanded = '1'
    applyNotesCollapse()
  }
  if (notes && notesMore) {
    notesMore.addEventListener('click', () => {
      notesMore.dataset.expanded = '1'
      applyNotesCollapse()
      for (const li of Array.from(notes.children).slice(NOTES_FIRST)) li.classList.add('pg-art-is-revealed')
    })
    narrowNotes.addEventListener('change', applyNotesCollapse)
    applyNotesCollapse()
  }

  /* "Hide notes": from 1100px a reader can hide the margin column; the choice
     is remembered (localStorage, best effort). Without script the margin
     shows and the button stays [hidden]. */
  const page = article.closest('.page')
  const toggle = $('[data-notes-toggle]')
  const NOTES_KEY = 'pxke_notes_hidden'
  const notesHidden = () => !!page?.classList.contains('pg-art-notes-hidden')
  const setNotes = (hidden, remember) => {
    if (!page || !toggle) return
    /* Animate only a reader's own toggle, never the stored choice on load. */
    if (remember) page.classList.add('pg-art-notes-anim')
    page.classList.toggle('pg-art-notes-hidden', hidden)
    /* The boot script's pre-paint hint (html[data-notes]) hands over to the page class. */
    delete root.dataset.notes
    toggle.setAttribute('aria-expanded', String(!hidden))
    toggle.title = hidden ? toggle.dataset.show : toggle.dataset.hide
    if (remember) {
      try { localStorage.setItem(NOTES_KEY, hidden ? '1' : '0') } catch (err) { console.warn('[article] notes choice not stored:', err) }
    }
    if (!hidden) relayout()
  }
  if (toggle && page) {
    let saved = false
    try { saved = localStorage.getItem(NOTES_KEY) === '1' } catch (err) { console.warn('[article] notes choice not read:', err) }
    toggle.hidden = false
    setNotes(saved, false)
    toggle.addEventListener('click', () => setNotes(!notesHidden(), true))
  }
  /* A number clicked while the margin is hidden reopens it first. */
  const reopenNotes = () => {
    if (!wide() || !notesHidden()) return false
    setNotes(false, true)
    return true
  }

  /* A fact-check mark opens its note: the margin from 1100px, else the list after the text. */
  const reveal = (el, flash = false) => {
    if (!el) return
    expandNotesFor(el)
    const r = el.getBoundingClientRect()
    if (r.top < 64 || r.bottom > innerHeight) el.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' })
    el.focus({ preventScroll: true })
    if (flash) {
      el.classList.add('pg-art-is-target')
      setTimeout(() => el.classList.remove('pg-art-is-target'), 1600)
    }
  }
  /* The mark and its fact check share the active state: the mark underlines, the note is outlined. */
  const setActive = (n) => {
    for (const m of $$('.pg-art-receipt-mark.pg-art-is-active, .pg-art-receipt-cell.pg-art-is-active', body)) m.classList.remove('pg-art-is-active')
    for (const li of $$('.px-anno.pg-art-is-on', article)) li.classList.remove('pg-art-is-on')
    if (!n) return
    for (const m of $$(`mark.pg-art-receipt-mark[data-n="${n}"], .pg-art-receipt-cell[data-receipt-n="${n}"]`, body)) m.classList.add('pg-art-is-active')
    doc.getElementById(`margin-receipt-${n}`)?.classList.add('pg-art-is-on')
  }
  article.addEventListener('click', (e) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    const cite = e.target.closest('sup.pg-art-cite-ref a')
    if (cite) {
      e.preventDefault()
      reveal(doc.getElementById(`src-${cite.dataset.n}`), true)
      return
    }
    const ref = e.target.closest('sup.pg-art-receipt-ref a, a.pg-art-receipt-cap-ref')
    const link = e.target.closest('a')
    const mark = e.target.closest('mark.pg-art-receipt-mark, .pg-art-receipt-cell')
    const n = ref ? ref.dataset.n : link ? null : mark?.dataset.n ?? mark?.getAttribute('data-receipt-n')
    if (!n) return
    e.preventDefault()
    setActive(n)
    const reopened = reopenNotes()
    const note = doc.getElementById(`margin-receipt-${n}`)
    if (notes && note) reveal(note, reopened)
  })

  /* Glossary popover: opens in place with what the link carries (term, title),
     then swaps in the full entry from GET /__html/partials/glossary/:slug. */
  let pop = null
  function closePopover() { pop?.remove(); pop = null }
  const wrap = $('.pg-art-md-wrap')
  wrap?.addEventListener('click', (e) => {
    if (e.target.closest('.pg-art-glossary-popover-close')) { closePopover(); return }
    const a = e.target.closest('a.pg-art-glossary-term')
    if (!a) { if (!e.target.closest('.pg-art-glossary-popover')) closePopover(); return }
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    const slug = a.dataset.glossarySlug
    /* Margin hidden: the term reopens it at its glossary note. */
    if (reopenNotes()) {
      closePopover()
      reveal(doc.getElementById(`margin-gloss-${slug}`), true)
      return
    }
    const same = pop && pop.dataset.slug === slug
    closePopover()
    if (same) return
    const r = a.getBoundingClientRect()
    const w = wrap.getBoundingClientRect()
    pop = doc.createElement('div')
    pop.className = 'pg-art-glossary-popover pg-art-body'
    pop.dataset.slug = slug
    pop.setAttribute('role', 'dialog')
    pop.setAttribute('aria-label', a.textContent)
    pop.style.top = `${r.bottom - w.top + 8}px`
    pop.style.left = `${r.left - w.left}px`
    const line = (cls, text) => { const p = doc.createElement('p'); p.className = cls + ' pg-art-body'; p.textContent = text; return p }
    const note = doc.getElementById(`margin-gloss-${slug}`)
    const def = line('pg-art-glossary-popover-def', a.title || note?.querySelector('.pg-art-gloss-def')?.textContent || '')
    const loading = doc.createElement('span')
    loading.className = 'pg-art-glossary-popover-loading pg-art-body'
    loading.textContent = '…'
    def.append(loading)
    pop.append(
      line('px-kicker pg-art-glossary-popover-kicker', wrap.dataset.glossaryLabel || 'Glossary'),
      line('pg-art-glossary-popover-term', note?.querySelector('dfn')?.textContent || a.textContent),
      def,
    )
    wrap.append(pop)
    const url = `${wrap.dataset.glossaryPartial}${encodeURIComponent(slug)}?lang=${encodeURIComponent(wrap.dataset.glossaryLang || 'en')}`
    const target = pop
    if (window.htmx) {
      htmx.ajax('GET', url, { target, swap: 'innerHTML' }).catch((err) => {
        console.warn(`[article] glossary ${slug} failed:`, err)
        loading.remove()
      })
    } else {
      loading.remove()
    }
  })

  /* Wide tables: the scroll hint while there is more to the right. */
  for (const frame of $$('.pg-art-table-frame', body)) {
    const sc = $('.pg-art-table-scroll', frame)
    const sync = () => frame.classList.toggle('pg-art-is-overflow', sc.scrollWidth > sc.clientWidth + 8 && sc.scrollLeft + sc.clientWidth < sc.scrollWidth - 8)
    sc.addEventListener('scroll', sync, { passive: true })
    new ResizeObserver(sync).observe(sc)
  }
})()
